import React, { useState } from "react";
import { useAuth } from "../../../context/authContext";
import { Alert, Box, Button, IconButton, Tooltip, Typography, Divider, CircularProgress } from "@mui/material";
import PageTitle from "../../../components/PageTitle/PageTitle";
import ExpandableDataGrid from "../../../components/ExpandableDataGrid/ExpandableDataGrid";
import SummarizeIcon from "@mui/icons-material/Summarize";
import "../../Services/Services.css";
import { useTranslation } from "react-i18next";
import NotAuthorized from "../../../components/NotAuthorized/NotAuthorized";
import { formatDate } from "../../../utilities/globalServiceUtils";
import Swal from "sweetalert2";
import { getWorkorderReport } from "../../../services/servicesService";
import { useWorkordersData } from "../../../hooks/useServiceRecords";
import DataGridSkeleton from "../../../components/PageSkeletons/DataGridSkeleton";
import DocumentViewModal from "../../../components/DocumentViewModal/DocumentViewModal";
import WorkorderReportsModal from "../../../components/WorkorderReportsModal/WorkorderReportsModal";

const WorkOrders = () => {
    const { hasPermission } = useAuth();
    const { t } = useTranslation();
    const { workorders, workordersLoading } = useWorkordersData();
    const [reportLoadingWo, setReportLoadingWo] = useState(null);
    const [reportLoadingType, setReportLoadingType] = useState(null);
    const [reportListRow, setReportListRow] = useState(null);
    const [reportDocument, setReportDocument] = useState(null);
    const [reportModalOpen, setReportModalOpen] = useState(false);

    const columns_wo = [
        { field: "wo_number", headerName: t('TABLE_HEADERS.WO_NUMBER'), flex: 1 },
        { field: "wo_type", headerName: t('TABLE_HEADERS.TYPE'), flex: 1, filterable: true },
        { field: "machine_id", headerName: t('TABLE_HEADERS.MACHINE'), flex: 1, filterable: true },
        { field: "booked_resource", headerName: t('TABLE_HEADERS.BOOKED_RESOURCE', { defaultValue: 'Booked Resource' }), flex: 1, filterable: true },
        // Service Start Date column — commented out per request
        // {
        //     field: "service_start_date", headerName: "Service Start Date", flex: 1, valueFormatter: (value) => formatDate(value),
        //     sortComparator: (v1, v2) => {
        //         const d1 = v1 ? new Date(v1).getTime() : 0;
        //         const d2 = v2 ? new Date(v2).getTime() : 0;
        //         return d1 - d2;
        //     },
        // },
        {
            field: "service_end_date", headerName: "Service End Date", flex: 1, valueFormatter: (value) => formatDate(value),
            sortComparator: (v1, v2) => {
                const d1 = v1 ? new Date(v1).getTime() : 0;
                const d2 = v2 ? new Date(v2).getTime() : 0;
                return d1 - d2;
            },
        },
        // {
        //     field: "created_date", headerName: t('TABLE_HEADERS.CREATED_DATE'), flex: 1, valueFormatter: (value) => formatDate(value),
        //     sortComparator: (v1, v2) => {
        //         const d1 = v1 ? new Date(v1).getTime() : 0;
        //         const d2 = v2 ? new Date(v2).getTime() : 0;
        //         return d1 - d2;
        //     },
        // },
        // {
        //     field: "closed_date", headerName: t('TABLE_HEADERS.CLOSED_DATE'), flex: 1, valueFormatter: (value) => formatDate(value),
        //     sortComparator: (v1, v2) => {
        //         const d1 = v1 ? new Date(v1).getTime() : 0;
        //         const d2 = v2 ? new Date(v2).getTime() : 0;
        //         return d1 - d2;
        //     },
        // },
        {
            field: "actions", headerName: t('TABLE_HEADERS.ACTIONS'), width: 120, headerAlign: "left",
            align: "left", filterable: false, renderCell: (params) => {
                const isService = String(params?.row?.wo_type || "").toLowerCase() === "service";
                const hasReport = Array.isArray(params?.row?.reports) && params.row.reports.length > 0;
                if (!isService || !hasReport) return null;
                return (
                    <Box sx={{ display: "flex", flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', height: '100%' }}>
                        <Box sx={{ display: "flex", justifyContent: 'flex-start', alignItems: 'center' }}>
                            <Tooltip title={t('BUTTON.VIEW_SERVICE_REPORT')}>
                                <span>
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const reports = params.row.reports || [];
                                            if (reports.length === 1) {
                                                handleViewReport(params.row, reports[0].type, reports[0].id);
                                            } else {
                                                setReportListRow(params.row);
                                            }
                                        }}
                                    >
                                        <SummarizeIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    </Box>
                );
            }
        },
    ];

    const handleViewReport = async (row, reportType, reportId) => {
        try {
            const woNumber = row?.wo_number;
            setReportLoadingWo(woNumber || null);
            setReportLoadingType(reportType || null);
            const resp = await getWorkorderReport(row?.wo_number, reportType, reportId);
            const payload = resp?.data?.data || resp?.data || {};
            const reports = Array.isArray(payload.reports)
                ? payload.reports.filter((report) => report?.url)
                : payload.url ? [{ type: reportType, file_name: "Service Report.pdf", url: payload.url }] : [];

            if (reports.length > 0) {
                const selectedReport = reports.find((report) => String(report.id) === String(reportId))
                    || reports.find((report) => String(report.type).toLowerCase() === String(reportType).toLowerCase())
                    || reports[0];

                setReportDocument({
                    name: selectedReport.file_name || `${woNumber}-${selectedReport.type}.pdf`,
                    url: selectedReport.url,
                    content_type: "application/pdf",
                    attachment_type: "file",
                });
                setReportModalOpen(true);
            } else {
                Swal.fire({
                    icon: 'warning',
                    title: 'Report Not Found',
                    text: 'The requested work order report could not be found.',
                    confirmButtonText: t('GENERAL.OK'),
                });
            }
        } catch (e) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'An error occurred while fetching the report. Please try again later.',
                confirmButtonText: t('GENERAL.OK'),
            });
        } finally {
            setReportLoadingWo(null);
            setReportLoadingType(null);
        }
    };

    const handleReportListView = (report) => {
        setReportListRow(null);
        handleViewReport(reportListRow, report.type, report.id);
    };

    // Removed Create New Workorder button
    const canSeeWorkOrders = !!hasPermission('service_management.view_workorders');

    // If user can't see either tab, block access entirely
    if (!canSeeWorkOrders) {
        return <NotAuthorized />;
    }

    if (workordersLoading) {
        return (
            <DataGridSkeleton
                containerClassName="services-container"
                actionButtons={0}
                columns={6}
                rows={6}
            />
        );
    }




    // Ensure valid tab index when permissions change dynamically
    const renderWorkorderMasterDetails = (woRow) => (
        <Box sx={{
            p: 2, width: '100%', border: "1px solid #e6e8eb",
            borderRadius: 2,
        }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="body1" fontWeight={"bold"}>Work Order Details: {woRow?.wo_number}</Typography>
                <Typography variant="body2" color="text.secondary">Please review the work order details below</Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", columnGap: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.WO_NUMBER")}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{woRow?.wo_number}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.STATUS")}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{woRow?.wo_status}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.TYPE")}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{woRow?.wo_type}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.MACHINE")}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{woRow?.machine_id || "-"}</Typography>
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} flexItem />
            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", columnGap: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.BOOKED_RESOURCE", { defaultValue: "Booked Resource" })}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{woRow?.booked_resource}</Typography>
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} flexItem />
            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", columnGap: 2 }}>
                {/* Service Start Date — commented out per request
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">Service Start Date</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{formatDate(woRow?.service_start_date)}</Typography>
                </Box>
                */}
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">Service End Date</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{formatDate(woRow?.service_end_date)}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.CREATED_DATE")}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{formatDate(woRow?.created_date)}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.CLOSED_DATE")}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{formatDate(woRow?.closed_date) || "-"}</Typography>
                </Box>
            </Box>
            <Divider sx={{ my: 2 }} flexItem />
            <Typography variant="subtitle2">{t("TABLE_HEADERS.DESCRIPTIONS")}</Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>{woRow?.wo_description || "-"}</Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                {String(woRow?.wo_type || "").toLowerCase() === "service" && Array.isArray(woRow?.reports) && woRow.reports.length === 1 && woRow.reports.some((report) => report?.type === "service") && (
                    <Button
                        variant="contained"
                        onClick={() => handleViewReport(woRow, "service")}
                        startIcon={<SummarizeIcon />}
                        disabled={reportLoadingWo === woRow?.wo_number}
                    >
                        {reportLoadingWo === woRow?.wo_number && reportLoadingType === "service" ? t("BUTTON.LOADING", { defaultValue: "Loading..." }) : t("BUTTON.VIEW_SERVICE_REPORT")}
                    </Button>
                )}
                {String(woRow?.wo_type || "").toLowerCase() === "service" && Array.isArray(woRow?.reports) && woRow.reports.length === 1 && woRow.reports.some((report) => report?.type === "inspection") && (
                    <Button
                        variant="contained"
                        onClick={() => handleViewReport(woRow, "inspection")}
                        startIcon={<SummarizeIcon />}
                        disabled={reportLoadingWo === woRow?.wo_number}
                    >
                        {reportLoadingWo === woRow?.wo_number && reportLoadingType === "inspection" ? t("BUTTON.LOADING", { defaultValue: "Loading..." }) : "View Inspection Report"}
                    </Button>
                )}
            </Box>
        </Box>
    );

    return (
        <div className="services-container">
            <PageTitle title={t('TITLES.WORK_ORDERS_REPORTS')} />


            {workorders.length === 0 && !workordersLoading ? (
                <Alert severity="info" className="error-alert" sx={{ marginTop: "10px" }}>
                    {t('ALERT_MESSAGES.NO_WORKORDERS_FOUND')}
                </Alert>
            ) : (
                <>
                <ExpandableDataGrid
                    key="workordersGrid"
                    data={workorders}
                    columns={columns_wo}
                    pageSize={5}
                    showSearchBar={true}
                    checkboxSelection={false}
                    customButtons={[]}
                    rowIdField="id"
                    sortBy={[{ field: "wo_number", sort: "desc" }]}
                    masterDetails={renderWorkorderMasterDetails}
                    allowMultipleExpand={false}
                />
                <WorkorderReportsModal
                    open={!!reportListRow}
                    onClose={() => setReportListRow(null)}
                    woNumber={reportListRow?.wo_number}
                    reports={reportListRow?.reports || []}
                    onView={handleReportListView}
                />
                <DocumentViewModal
                    open={reportModalOpen}
                    setOpen={setReportModalOpen}
                    document={reportDocument}
                    onClose={() => { setReportModalOpen(false); setReportDocument(null); }}
                    displayName={reportDocument?.name}
                />
                </>
            )}
        </div>
    );

};

export default WorkOrders;
